import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import StartupProfile from '../components/profile/StartupProfile';
import InvestorProfile from '../components/profile/InvestorProfile';
import {
  StartupProfile as StartupProfileType,
  InvestorProfile as InvestorProfileType,
} from '../types/firestore';
import { formatDate } from '../utils/date';
import { formatFileSize } from '../utils/formatFileSize';
import { pitchDeckFrameSrc } from '../utils/pitchDeckFrameSrc';
import ImageCropModal from '../components/admin/ImageCropModal';

const ApproveUserPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userType, setUserType] = useState<'startup' | 'investor' | null>(null);
  const [profileData, setProfileData] = useState<StartupProfileType | InvestorProfileType | null>(
    null
  );
  const [approving, setApproving] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [pitchDeckBytes, setPitchDeckBytes] = useState<number | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const [startupSettled, investorSettled] = await Promise.allSettled([
          httpsCallable(functions, 'getStartupProfile')({ userId: id }),
          httpsCallable(functions, 'getInvestorProfile')({ userId: id }),
        ]);

        const startupData =
          startupSettled.status === 'fulfilled' ? (startupSettled.value.data as any) : null;
        const investorData =
          investorSettled.status === 'fulfilled' ? (investorSettled.value.data as any) : null;

        if (startupData?.success && startupData?.profile) {
          setUserType('startup');
          setProfileData(startupData.profile);
        } else if (investorData?.success && investorData?.profile) {
          setUserType('investor');
          setProfileData(investorData.profile);
        } else {
          throw new Error(
            'User profile not found. The user may not have completed their profile yet.'
          );
        }
      } catch (err: any) {
        console.error('Error fetching user data:', err);
        setError(err.message || 'Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [id]);

  // Create object URL for thumbnail preview
  useEffect(() => {
    if (thumbnailFile) {
      const objectUrl = URL.createObjectURL(thumbnailFile);
      setThumbnailPreview(objectUrl);

      // Cleanup function to revoke the object URL
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else {
      setThumbnailPreview(null);
    }
  }, [thumbnailFile]);

  // Resolve pitch deck size for admin review (helps spot image-heavy uploads)
  useEffect(() => {
    const pitchDeckUrl =
      userType === 'startup' ? (profileData as StartupProfileType | null)?.pitchDeckUrl : undefined;

    if (!pitchDeckUrl) {
      setPitchDeckBytes(null);
      return;
    }

    let cancelled = false;
    setPitchDeckBytes(null);

    (async () => {
      try {
        const response = await fetch(pitchDeckUrl, { method: 'HEAD' });
        const lengthHeader = response.headers.get('content-length');
        const bytes = lengthHeader ? Number(lengthHeader) : NaN;
        if (!cancelled && Number.isFinite(bytes) && bytes > 0) {
          setPitchDeckBytes(bytes);
        }
      } catch {
        // Non-blocking — size badge is informational only
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userType, profileData]);

  const handleApproveUser = async () => {
    if (!id || !userType) return;

    // For startups, thumbnail is required
    if (userType === 'startup' && !thumbnailFile) return;

    try {
      setApproving(true);
      setError(null);

      if (userType === 'investor') {
        const updateInvestorProfile = httpsCallable(functions, 'updateInvestorProfile');
        const result = await updateInvestorProfile({
          userId: id,
          updates: {
            approved: true,
            approvedAt: new Date(),
          },
        });

        const data = result.data as any;

        if (data.success && data.profile) {
          setProfileData(data.profile);
          new BroadcastChannel('prairi-admin').postMessage({ type: 'USER_APPROVED', userId: id });
        } else {
          throw new Error(data.error || 'Failed to approve user');
        }
      } else if (userType === 'startup') {
        // First, upload thumbnail if provided
        if (thumbnailFile) {
          // Convert File to base64
          const reader = new FileReader();
          const base64Data = await new Promise<string>((resolve, reject) => {
            reader.onload = () => {
              const result = reader.result as string;
              const base64 = result.split(',')[1]; // Remove data:image/...;base64, prefix
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(thumbnailFile);
          });

          // Upload thumbnail
          const uploadStartupThumbnail = httpsCallable(functions, 'uploadStartupThumbnail');
          const uploadResult = await uploadStartupThumbnail({
            userId: id,
            file: {
              name: thumbnailFile.name,
              contentType: thumbnailFile.type,
              data: base64Data,
            },
          });

          const uploadData = uploadResult.data as any;
          if (!uploadData.success) {
            throw new Error(uploadData.error || 'Failed to upload thumbnail');
          }
        }

        // Then update the approval status
        const updateStartupProfile = httpsCallable(functions, 'updateStartupProfile');
        const updates: any = {
          approved: true,
          approvedAt: new Date(),
        };

        // Always set featured field (true or false)
        updates.featured = isFeatured === true;

        const result = await updateStartupProfile({
          userId: id,
          updates,
        });

        const data = result.data as any;

        if (data.success && data.profile) {
          setProfileData(data.profile);
          new BroadcastChannel('prairi-admin').postMessage({ type: 'USER_APPROVED', userId: id });
        } else {
          throw new Error(data.error || 'Failed to approve user');
        }
      }
    } catch (err: any) {
      console.error('Error approving user:', err);
      setError(err.message || 'Failed to approve user');
    } finally {
      setApproving(false);
    }
  };

  if (!loading && (error || !profileData)) {
    return (
      <div className="min-h-screen bg-dark-950 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-gray-900 rounded-lg p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-white mb-2">Error Loading Profile</h1>
          <p className="text-gray-400 mb-6">{error || 'Profile data not found.'}</p>
          <p className="text-gray-500 text-sm">User ID: {id}</p>
        </div>
      </div>
    );
  }

  const startupProfile = userType === 'startup' ? (profileData as StartupProfileType) : null;
  const isStartupApproved = startupProfile?.approved === true;
  const adminPendingReview = userType === 'startup' && !isStartupApproved;
  const adminEffectiveThumbnail =
    thumbnailPreview?.trim() || startupProfile?.thumbnailUrl?.trim() || '';
  const adminStartupRightPanel = startupProfile ? (
    <div className="hidden lg:flex lg:flex-col lg:w-[65%] sticky top-[3.5rem] h-[calc(100vh-3.5rem)] min-h-0 border-l-8 border-black">
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-visible-dark flex flex-col gap-4 p-2">
        {startupProfile.pitchDeckUrl ? (
          <div className="w-full flex flex-col gap-2">
            {pitchDeckBytes != null && (
              <p className="text-xs text-gray-400 px-1">
                Pitch deck file size:{' '}
                <span className="text-white font-medium">{formatFileSize(pitchDeckBytes)}</span>
                {pitchDeckBytes >= 3 * 1024 * 1024
                  ? ' — large / image-heavy decks can flip slowly in the browser viewer'
                  : ''}
              </p>
            )}
            <div className="w-full aspect-[16/9] overflow-hidden border border-gray-800">
              <iframe
                src={pitchDeckFrameSrc(startupProfile.pitchDeckUrl)}
                className="w-full h-full border-0 block"
                title="Pitch Deck"
              />
            </div>
          </div>
        ) : adminEffectiveThumbnail ? (
          <div className="w-full aspect-[16/9] overflow-hidden border border-gray-800 relative">
            <img
              src={adminEffectiveThumbnail}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <p className="text-gray-300 text-sm">No pitch deck uploaded yet</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 border border-gray-800 rounded-lg">
            <p className="text-gray-500 text-sm">No pitch deck uploaded yet</p>
          </div>
        )}
      </div>
    </div>
  ) : undefined;

  return (
    <div className="min-h-screen bg-[#262626]">
      {/* Admin Action Bar (Placeholder for future actions) */}
      <div className="bg-gray-900 border-b border-gray-800 p-4 sticky top-0 z-50 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded">
              ADMIN VIEW
            </span>
            <span className="text-gray-300 text-sm">
              Reviewing: <span className="text-white font-medium">{userType?.toUpperCase()}</span>
            </span>
          </div>
          <div className="flex space-x-3 items-center">
            {userType === 'startup' &&
              (() => {
                const startupProfile = profileData as StartupProfileType;
                const isApproved = startupProfile?.approved === true;

                return (
                  <>
                    {!isApproved && (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => {
                            const file = e.target.files?.[0] || null;
                            if (file) {
                              // Create preview URL and show crop modal
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setImagePreview(reader.result as string);
                                setShowCropModal(true);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                        />
                        <span className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg transition-colors duration-200 inline-block">
                          Choose thumbnail
                        </span>
                      </label>
                    )}
                    {thumbnailPreview && (
                      <div className="relative">
                        <img
                          src={thumbnailPreview}
                          alt="Thumbnail preview"
                          className="w-32 h-18 object-cover rounded border border-gray-600"
                        />
                      </div>
                    )}
                    {!isApproved && (
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isFeatured}
                          onChange={e => setIsFeatured(e.target.checked)}
                          className="w-4 h-4 text-emerald-500 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500 focus:ring-2"
                        />
                        <span className="text-white text-sm font-medium">Featured startup?</span>
                      </label>
                    )}
                  </>
                );
              })()}
            {userType === 'investor' &&
              (() => {
                const investorProfile = profileData as InvestorProfileType;
                const isApproved = investorProfile?.approved === true;
                const approvedAt = investorProfile?.approvedAt;
                const formattedDate = approvedAt ? formatDate(approvedAt) : null;

                if (isApproved && formattedDate) {
                  return (
                    <div className="bg-emerald-500/20 border border-emerald-500 text-emerald-400 font-semibold px-4 py-2 rounded-lg flex items-center gap-2">
                      <span>User approved on {formattedDate}</span>
                    </div>
                  );
                }

                return (
                  <button
                    onClick={handleApproveUser}
                    disabled={approving}
                    className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
                  >
                    {approving ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                        <span>Approving...</span>
                      </>
                    ) : (
                      <span>Approve user</span>
                    )}
                  </button>
                );
              })()}
            {userType === 'startup' &&
              (() => {
                const startupProfile = profileData as StartupProfileType;
                const isApproved = startupProfile?.approved === true;
                const approvedAt = startupProfile?.approvedAt;
                const formattedDate = approvedAt ? formatDate(approvedAt) : null;

                if (isApproved && formattedDate) {
                  return (
                    <div className="bg-emerald-500/20 border border-emerald-500 text-emerald-400 font-semibold px-4 py-2 rounded-lg flex items-center gap-2">
                      <span>User approved on {formattedDate}</span>
                    </div>
                  );
                }

                return (
                  <button
                    onClick={handleApproveUser}
                    disabled={approving || !thumbnailFile}
                    className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
                  >
                    {approving ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                        <span>Approving...</span>
                      </>
                    ) : (
                      <span>Approve user</span>
                    )}
                  </button>
                );
              })()}
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="pt-6">
        {loading ? (
          <div className="max-w-4xl mx-auto px-6 py-8 space-y-4 animate-pulse">
            <div className="h-8 bg-gray-800 rounded w-1/3" />
            <div className="h-4 bg-gray-800 rounded w-2/3" />
            <div className="h-4 bg-gray-800 rounded w-1/2" />
            <div className="h-48 bg-gray-800 rounded" />
            <div className="h-4 bg-gray-800 rounded w-3/4" />
            <div className="h-4 bg-gray-800 rounded w-2/3" />
          </div>
        ) : userType === 'startup' ? (
          <StartupProfile
            profile={profileData as StartupProfileType}
            hideActions={true}
            compactDeck={true}
            rightPanel={adminStartupRightPanel}
            leftPanelTall={true}
            previewThumbnailUrl={thumbnailPreview ?? undefined}
            adminPendingReview={adminPendingReview}
          />
        ) : (
          <InvestorProfile profile={profileData as InvestorProfileType} />
        )}
      </div>

      {/* Image Crop Modal */}
      {showCropModal && imagePreview && (
        <ImageCropModal
          imageSrc={imagePreview}
          onClose={() => {
            setShowCropModal(false);
            setImagePreview(null);
          }}
          onCropComplete={croppedFile => {
            setThumbnailFile(croppedFile);
            setShowCropModal(false);
            setImagePreview(null);
          }}
          aspectRatio={16 / 9}
        />
      )}
    </div>
  );
};

export default ApproveUserPage;
